import { ensureFirebaseInitialized } from '../firebase';
import { getFirestore, doc, getDoc, collection, query, where, getDocs, arrayUnion, writeBatch, updateDoc, setDoc, arrayRemove } from 'firebase/firestore';
import { getApps } from 'firebase/app';
import logger from './logger';

/**
 * Access levels for different storage areas
 */
export const ACCESS_LEVELS = {
  PERSONAL: 'personal',    // My Documents, My Photos (private)
  FAMILY: 'family',       // Family Documents, Family Photos (shared)
  VAULT: 'vault'         // Individual Vault (per-user, group-based)
};

/**
 * Storage folder types
 */
export const STORAGE_FOLDERS = {
  DOCUMENTS: 'documents',
  PHOTOS: 'photos'
};

/**
 * Permission types for different actions
 */
export const PERMISSIONS = {
  READ: 'read',
  WRITE: 'write',
  UPLOAD: 'upload',
  DELETE: 'delete',
  ADMIN: 'admin'
};

/**
 * Default vault groups with their permissions
 */
export const DEFAULT_VAULT_GROUPS = {
  OWNER: {
    name: 'Owner',
    permissions: [PERMISSIONS.READ, PERMISSIONS.WRITE, PERMISSIONS.UPLOAD, PERMISSIONS.DELETE, PERMISSIONS.ADMIN]
  },
  IMMEDIATE_FAMILY: {
    name: 'Immediate Family',
    permissions: [PERMISSIONS.READ, PERMISSIONS.UPLOAD]
  },
  EXTENDED_FAMILY: {
    name: 'Extended Family',
    permissions: [PERMISSIONS.READ]
  }
};

/**
 * Check if users are in the same family
 */
const areInSameFamily = async (userId1, userId2) => {
  const db = getFirestore();
  try {
    const user1Doc = await getDoc(doc(db, 'users', userId1));
    const user2Doc = await getDoc(doc(db, 'users', userId2));
    
    if (!user1Doc.exists() || !user2Doc.exists()) return false;
    
    return user1Doc.data().familyCode === user2Doc.data().familyCode;
  } catch (error) {
    logger.error('Error checking family relationship:', error);
    return false;
  }
};

/**
 * Check if user has access to a specific folder
 * @param {Object} user - Current user object
 * @param {string} familyCode - Family code (e.g. SMITH002)
 * @param {string} folderId - The ID of the folder to check
 * @returns {Promise<boolean>} - Whether user has access
 */
export const hasFolderAccess = async (user, familyCode, folderId) => {
  if (!user || !familyCode || !folderId) return false;

  const db = getFirestore();
  try {
    // Get folder metadata
    const folderDoc = await getDoc(doc(db, `families/${familyCode}/folders/${folderId}`));
    if (!folderDoc.exists()) return false;

    const folderData = folderDoc.data();
    
    // Owner always has access
    if (folderData.ownerId === user.uid) return true;
    
    // Check if folder is shared directly with this user
    if (folderData.sharedWith?.includes(user.uid)) return true;
    
    // Check if folder is shared with any of user's family groups
    if (user.familyGroups?.some(groupId => folderData.sharedWithGroups?.includes(groupId))) return true;
    
    // Check parent folder permissions if inheritance is enabled
    if (folderData.inheritParentPermissions && folderData.parentId) {
      return hasFolderAccess(user, familyCode, folderData.parentId);
    }

    return false;
  } catch (error) {
    logger.error('Error checking folder access:', error);
    return false;
  }
};

/**
 * Share a folder with specific family members
 */
export const shareFolderWithMembers = async (user, familyCode, folderId, memberIds, options = {}) => {
  const { recursive = false, inheritPermissions = true } = options;
  
  if (!user || !familyCode || !folderId || !memberIds?.length) {
    throw new Error('Missing required parameters');
  }

  const db = getFirestore();
  try {
    // Verify all members are in the same family
    for (const memberId of memberIds) {
      if (!(await areInSameFamily(user.uid, memberId))) {
        throw new Error(`User ${memberId} is not in the same family`);
      }
    }

    const folderRef = doc(db, `families/${familyCode}/folders/${folderId}`);
    const folderDoc = await getDoc(folderRef);
    
    if (!folderDoc.exists()) {
      throw new Error('Folder not found');
    }
    
    // Only owner can modify sharing settings
    if (folderDoc.data().ownerId !== user.uid) {
      throw new Error('Only folder owner can modify sharing settings');
    }

    // Update folder sharing
    await folderRef.update({
      sharedWith: memberIds,
      inheritParentPermissions: inheritPermissions,
      updatedAt: new Date(),
      lastModifiedBy: user.uid
    });

    // If recursive, update all subfolders
    if (recursive) {
      const subfolders = await getDocs(
        query(
          collection(db, `families/${familyCode}/folders`),
          where('parentId', '==', folderId)
        )
      );

      for (const subfolder of subfolders.docs) {
        await shareFolderWithMembers(user, familyCode, subfolder.id, memberIds, options);
      }
    }
  } catch (error) {
    logger.error('Error sharing folder:', error);
    throw error;
  }
};

/**
 * Share vault access with family members
 */
export const shareVaultAccess = async (user, memberIds) => {
  if (!user || !memberIds?.length) {
    throw new Error('Missing required parameters');
  }

  const db = getFirestore();
  try {
    // Verify all members are in the same family
    for (const memberId of memberIds) {
      if (!(await areInSameFamily(user.uid, memberId))) {
        throw new Error(`User ${memberId} is not in the same family`);
      }
    }

    // Update user's vault sharing settings
    const vaultRef = doc(db, 'vaults', user.uid);
    await vaultRef.update({
      sharedWith: memberIds,
      updatedAt: new Date()
    });

    // Add vault access to each member's permissions
    for (const memberId of memberIds) {
      const memberRef = doc(db, 'users', memberId);
      await memberRef.update({
        'vaultAccess': arrayUnion({
          vaultId: user.uid,
          grantedAt: new Date(),
          grantedBy: user.uid
        })
      });
    }
  } catch (error) {
    logger.error('Error sharing vault access:', error);
    throw error;
  }
};

/**
 * Check if user has specific permissions
 * @param {string} userId - The user's ID
 * @param {string} permissionType - The type of permission to check
 * @returns {Promise<boolean>} - Whether user has the permission
 */
export const checkUserPermissions = async (userId, permissionType) => {
  if (!userId || !permissionType) return false;
  
  const db = getFirestore();
  try {
    // Check if user has direct permission
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return false;
    
    const userData = userDoc.data();
    
    // Check if user has the permission directly
    if (userData.permissions?.includes(permissionType)) return true;
    
    // Check if user has the permission through a group
    if (userData.groups?.some(group => group.permissions?.includes(permissionType))) return true;
    
    // Check if user has the permission through family membership
    if (userData.familyCode) {
      const familyDoc = await getDoc(doc(db, 'families', userData.familyCode));
      if (familyDoc.exists()) {
        const familyData = familyDoc.data();
        
        // Check if user has vault access in the family
        if (permissionType === 'vault_access') {
          // Check if user is in the vault_access collection
          const vaultAccessDoc = await getDoc(doc(db, `families/${userData.familyCode}/vault_access/${userId}`));
          if (vaultAccessDoc.exists()) return true;
          
          // Check if user is in the immediate-family group
          if (userData.familyGroups?.includes('immediate-family')) return true;
          
          // Check if user has direct vault access
          if (userData.vaultAccess?.some(access => access.familyCode === userData.familyCode)) return true;
        }
        
        // Check other permissions
        if (familyData.permissions?.[userId]?.includes(permissionType)) return true;
      }
    }
    
    return false;
  } catch (error) {
    logger.error('Error checking user permissions:', error);
    return false;
  }
};

/**
 * Check if user has access to storage area
 */
export const hasStorageAccess = async (user, familyCode, storageArea) => {
  // Add more detailed validation
  if (!user) {
    logger.warn('No user provided to hasStorageAccess');
    return false;
  }

  if (!user.uid) {
    logger.warn('User object missing uid');
    return false;
  }

  if (!storageArea) {
    logger.warn('Storage area is required');
    return false;
  }

  const db = getFirestore();

  try {
    // Get user's document to check permissions
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      logger.warn('User document not found');
      return false;
    }
    
    const userData = userDoc.data();

    // Check access based on storage area
    switch (storageArea) {
      case ACCESS_LEVELS.PERSONAL:
        // Personal space - user always has access to their own space
        return true;

      case ACCESS_LEVELS.FAMILY:
        // Family space - check family code
        if (!familyCode) {
          logger.warn('Family code required for family space access');
          return false;
        }

        // If user has matching family code, they have access to family space
        // Group-based permissions provide granular control over specific actions
        if (userData.familyCode === familyCode) {
          return true;
        }

        // Super admins always have access
        if (userData.isSuperAdmin) {
          return true;
        }

        return false;

      case ACCESS_LEVELS.VAULT:
        // Vault - check relationship-based or group-based access
        if (!familyCode) {
          logger.warn('Family code required for vault access');
          return false;
        }

        // Super admins always have access
        if (userData.isSuperAdmin) {
          return true;
        }

        // Check if user has family code and is in the same family
        // Group-based permissions will control specific vault actions (read, write, upload, etc.)
        if (userData.familyCode === familyCode) {
          // User is in the family - check if they have any vault access granted
          // They may need to request access via the "REQUEST ACCESS" button
          return userData.hasVaultAccess === true || userData.vaultAccessGroups?.length > 0;
        }

        return false;

      default:
        logger.warn(`Invalid storage area: ${storageArea}`);
        return false;
    }
  } catch (error) {
    logger.error('Error checking storage access:', error);
    return false;
  }
};

/**
 * Check if a user has specific permission for an area
 */
export const checkPermission = async (user, storageArea, permission) => {
  if (!user || !user.uid) {
    logger.error('Invalid user object');
    return false;
  }

  // Allow default access for documents and photos in personal and family areas
  if ((storageArea === ACCESS_LEVELS.PERSONAL || storageArea === ACCESS_LEVELS.FAMILY) &&
      (permission === PERMISSIONS.READ || permission === PERMISSIONS.WRITE)) {
    return true;
  }

  try {
    // Check if Firebase is initialized
    if (!getApps().length) {
      logger.error('Firebase not initialized');
      return false;
    }

    const userDoc = await getApps()[0].firestore()
      .collection('users')
      .doc(user.uid)
      .get();

    if (!userDoc.exists) {
      logger.error('User document not found');
      return false;
    }

    const userData = userDoc.data();
    const permissions = userData.permissions || {};

    // For vault access, require explicit permissions
    if (storageArea === ACCESS_LEVELS.VAULT) {
      return permissions[storageArea]?.includes(permission) || false;
    }

    // For other operations like delete, share, etc. check explicit permissions
    if (permission === PERMISSIONS.DELETE || permission === PERMISSIONS.SHARE) {
      return permissions[storageArea]?.includes(permission) || false;
    }

    // Default to true for basic operations in personal/family areas
    return true;
  } catch (error) {
    logger.error('Error checking permission:', error);
    return false;
  }
};

/**
 * Get the storage path for a file based on storage area and type
 */
export const getStoragePath = (userId, familyCode, collectionType, fileId, storageArea) => {
  if (!userId || !familyCode || !collectionType || !fileId || !storageArea) {
    throw new Error('Missing required parameters for storage path');
  }

  // Check if Firebase is initialized
  if (!getApps().length) {
    throw new Error('Firebase not initialized');
  }

  const basePath = `${userId}/${familyCode}/${collectionType}`;
  
  switch (storageArea) {
    case STORAGE_FOLDERS.PERSONAL_VAULT:
      return `${basePath}/personal/${fileId}`;
    case STORAGE_FOLDERS.FAMILY_VAULT:
      return `${basePath}/family/${fileId}`;
    case STORAGE_FOLDERS.SHARED_FOLDERS:
      return `${basePath}/shared/${fileId}`;
    default:
      throw new Error(`Invalid storage area: ${storageArea}`);
  }
};

/**
 * Get Firestore collection path based on access level
 */
export const getFirestorePath = (userId, familyCode, collectionType, storageArea) => {
  // Validate inputs
  if (!userId) throw new Error('User ID is required');
  if (!collectionType) throw new Error('Collection type is required');
  
  switch (storageArea) {
    case ACCESS_LEVELS.PERSONAL:
      // Personal files: /users/{userId}/{collectionType}
      return `users/${userId}/${collectionType}`;
      
    case ACCESS_LEVELS.FAMILY:
      if (!familyCode) throw new Error('Family code is required for family storage area');
      // Family files: /families/{familyCode}/{collectionType}
      return `families/${familyCode}/${collectionType}`;
      
    case ACCESS_LEVELS.VAULT:
      if (!familyCode) throw new Error('Family code is required for vault storage area');
      // Vault files: /vault/{familyCode}/{collectionType}
      return `vault/${familyCode}/${collectionType}`;
      
    case ACCESS_LEVELS.PORTAL:
      if (!familyCode) throw new Error('Family code is required for portal storage area');
      // Portal files: /portal/{familyCode}/{collectionType}
      return `portal/${familyCode}/${collectionType}`;
      
    case ACCESS_LEVELS.DOCUMENTS:
      if (!familyCode) throw new Error('Family code is required for documents storage area');
      // Documents files: /documents/{familyCode}/{collectionType}
      return `documents/${familyCode}/${collectionType}`;
      
    case ACCESS_LEVELS.PHOTOS:
      if (!familyCode) throw new Error('Family code is required for photos storage area');
      // Photos files: /photos/{familyCode}/{collectionType}
      return `photos/${familyCode}/${collectionType}`;
      
    case ACCESS_LEVELS.EVENTS:
      if (!familyCode) throw new Error('Family code is required for events storage area');
      // Events files: /events/{familyCode}/{collectionType}
      return `events/${familyCode}/${collectionType}`;
      
    default:
      throw new Error(`Invalid storage area: ${storageArea}`);
  }
};

/**
 * Get default permissions based on relationship
 * @param {string} relationship - Family relationship
 * @returns {Object} Default permissions
 */
function getDefaultPermissionsByRelationship(relationship) {
  const basePermissions = {
    canViewProfile: true,
    canViewBasicInfo: true,
  };

  switch (relationship.toLowerCase()) {
    case 'spouse':
      return {
        ...basePermissions,
        canEditProfile: true,
        canManageVault: true,
        canManageFamily: true,
        canInviteMembers: true,
        canViewFinancial: true,
        canEditFinancial: true,
      };
    case 'parent':
    case 'child':
      return {
        ...basePermissions,
        canEditProfile: true,
        canManageVault: true,
        canViewFinancial: true,
        canInviteMembers: true,
      };
    case 'sibling':
      return {
        ...basePermissions,
        canEditProfile: true,
        canManageVault: false,
        canViewFinancial: false,
        canInviteMembers: true,
      };
    default:
      return {
        ...basePermissions,
        canEditProfile: false,
        canManageVault: false,
        canViewFinancial: false,
        canInviteMembers: false,
      };
  }
}

/**
 * Setup default group access for a new user
 * @param {string} userId - User ID
 * @param {string} familyCode - Family code
 * @param {string} relationship - Relationship to the family
 */
export const setupDefaultGroupAccess = async (userId, familyCode, relationship) => {
  if (!userId || !familyCode) {
    throw new Error('User ID and family code are required');
  }

  const { db } = await ensureFirebaseInitialized();
  const permissions = getDefaultPermissionsByRelationship(relationship || 'other');
  
  // Create or get Immediate Family group
  const immediateFamilyGroup = await getOrCreateGroup(familyCode, 'Immediate Family');
  
  // Add user to the group with appropriate permissions
  await addUserToGroup(userId, familyCode, immediateFamilyGroup.id, permissions);

  // If user has vault management permissions, create their personal vault
  if (permissions.canManageVault) {
    await createPersonalVault(userId, familyCode);
  }

  // Update user's document with permissions
  await updateDoc(doc(db, 'users', userId), {
    permissions,
    groups: arrayUnion(immediateFamilyGroup.id),
    updatedAt: new Date()
  });
}

/**
 * Create initial folder structure for a new user
 * @param {string} userId - The user's ID
 * @param {string} familyCode - The family code
 */
export const createInitialFolderStructure = async (userId, familyCode) => {
  const { db } = await ensureFirebaseInitialized();
  const batch = writeBatch(db);

  // Create personal folder metadata
  const personalMetadataRef = doc(db, `users/${userId}/metadata/folders`);
  batch.set(personalMetadataRef, {
    personal: {
      documents: {
        createdAt: new Date(),
        createdBy: userId,
        path: '/documents',
        name: 'Documents'
      },
      photos: {
        createdAt: new Date(),
        createdBy: userId,
        path: '/photos',
        name: 'Photos'
      }
    }
  });

  // Create family folder metadata if family code exists
  if (familyCode) {
    const familyMetadataRef = doc(db, `families/${familyCode}/metadata/folders`);
    batch.set(familyMetadataRef, {
      documents: {
        createdAt: new Date(),
        createdBy: userId,
        path: '/documents',
        name: 'Documents'
      },
      photos: {
        createdAt: new Date(),
        createdBy: userId,
        path: '/photos',
        name: 'Photos'
      }
    });

    // Create vault folder metadata
    const vaultMetadataRef = doc(db, `families/${familyCode}/vault/${userId}/metadata/folders`);
    batch.set(vaultMetadataRef, {
      documents: {
        createdAt: new Date(),
        createdBy: userId,
        path: '/documents',
        name: 'Documents'
      },
      photos: {
        createdAt: new Date(),
        createdBy: userId,
        path: '/photos',
        name: 'Photos'
      }
    });
  }

  await batch.commit();
};

async function getOrCreateGroup(familyCode, groupName) {
  const { db } = await ensureFirebaseInitialized();
  const groupRef = doc(db, `families/${familyCode}/groups/${groupName.toLowerCase()}`);
  const groupDoc = await getDoc(groupRef);
  
  if (!groupDoc.exists()) {
    await setDoc(groupRef, {
      name: groupName,
      createdAt: new Date(),
      updatedAt: new Date(),
      members: []
    });
  }
  
  return {
    id: groupRef.id,
    ...groupDoc.data()
  };
}

async function addUserToGroup(userId, familyCode, groupId, permissions) {
  const { db } = await ensureFirebaseInitialized();
  const groupRef = doc(db, `families/${familyCode}/groups/${groupId}`);
  await updateDoc(groupRef, {
    members: arrayUnion(userId),
    [`permissions.${userId}`]: permissions,
    updatedAt: new Date()
  });
}

async function createPersonalVault(userId, familyCode) {
  const { db } = await ensureFirebaseInitialized();
  const vaultRef = doc(db, `families/${familyCode}/vault/${userId}`);
  await setDoc(vaultRef, {
    ownerId: userId,
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true
  });
}

/**
 * Initialize a user's vault with default groups
 */
export const initializeUserVault = async (userId, familyCode) => {
  if (!userId || !familyCode) {
    throw new Error('Missing required parameters');
  }

  const db = getFirestore();
  try {
    const vaultRef = doc(db, 'users', userId, 'vault', 'config');
    const vaultDoc = await getDoc(vaultRef);

    if (!vaultDoc.exists()) {
      await setDoc(vaultRef, {
        owner: userId,
        familyCode,
        groups: DEFAULT_VAULT_GROUPS,
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }
  } catch (error) {
    logger.error('Error initializing user vault:', error);
    throw error;
  }
};

/**
 * Get a user's vault configuration
 */
export const getUserVaultConfig = async (userId) => {
  if (!userId) return null;

  const db = getFirestore();
  try {
    const vaultRef = doc(db, 'users', userId, 'vault', 'config');
    const vaultDoc = await getDoc(vaultRef);

    if (!vaultDoc.exists()) return null;
    return vaultDoc.data();
  } catch (error) {
    logger.error('Error getting user vault config:', error);
    return null;
  }
};

/**
 * Update vault group permissions
 */
export const updateVaultGroupPermissions = async (userId, groupId, permissions) => {
  if (!userId || !groupId || !permissions) {
    throw new Error('Missing required parameters');
  }

  const db = getFirestore();
  try {
    const vaultRef = doc(db, 'users', userId, 'vault', 'config');
    await updateDoc(vaultRef, {
      [`groups.${groupId}.permissions`]: permissions,
      updatedAt: new Date()
    });
  } catch (error) {
    logger.error('Error updating vault group permissions:', error);
    throw error;
  }
};

/**
 * Add member to vault group
 */
export const addMemberToVaultGroup = async (userId, groupId, memberId) => {
  if (!userId || !groupId || !memberId) {
    throw new Error('Missing required parameters');
  }

  const db = getFirestore();
  try {
    const vaultRef = doc(db, 'users', userId, 'vault', 'config');
    await updateDoc(vaultRef, {
      [`groups.${groupId}.members`]: arrayUnion(memberId),
      updatedAt: new Date()
    });
  } catch (error) {
    logger.error('Error adding member to vault group:', error);
    throw error;
  }
};

/**
 * Remove member from vault group
 */
export const removeMemberFromVaultGroup = async (userId, groupId, memberId) => {
  if (!userId || !groupId || !memberId) {
    throw new Error('Missing required parameters');
  }

  const db = getFirestore();
  try {
    const vaultRef = doc(db, 'users', userId, 'vault', 'config');
    await updateDoc(vaultRef, {
      [`groups.${groupId}.members`]: arrayRemove(memberId),
      updatedAt: new Date()
    });
  } catch (error) {
    logger.error('Error removing member from vault group:', error);
    throw error;
  }
}; 